import config from "core/config";
import { logger } from "library/core/utility";

const logPrefix = "[BlurServerService]:";

interface AssignResponse {
  success: boolean;
  worker_id: string;
  message: string;
  blur_output_url: string;
}

interface BlurStatusResponse {
  channel_name: string;
  blurring: boolean;
  worker_id?: string;
  output_url?: string;
  started_at?: number;
}

interface WorkersResponse {
  workers: Array<{
    worker_id: string;
    status: string;
    current_stream?: string;
    frames_processed?: number;
  }>;
  total: number;
  idle_count: number;
  processing_count: number;
}

class BlurServerService {
  private activeWorkerId: string | null = null;

  private get baseUrl(): string {
    return config.blurServerUrl;
  }

  private log(message: string, ...args: any[]) {
    logger.log("info", `${logPrefix} ${message}`, ...args);
  }

  private logError(message: string, ...args: any[]) {
    logger.log("error", `${logPrefix} ${message}`, ...args);
  }

  buildOriginStreamUrl = (
    wowzaIp: string,
    appName: string,
    channelName: string,
    resolution?: string
  ): string => {
    const streamName = resolution
      ? `${channelName}_${resolution}`
      : channelName;
    return `rtmp://${wowzaIp}:${config.rtmpPort}/${appName}/${channelName}/${streamName}`;
  };

  startBlur = async (
    originalStreamUrl: string,
    showOverlay?: boolean
  ): Promise<{ workerId: string; blurOutputUrl: string }> => {
    this.log("startBlur started", { originalStreamUrl, showOverlay });

    const response = await fetch(`${this.baseUrl}/api/worker/assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        original_stream_url: originalStreamUrl,
        show_overlay: showOverlay ?? false,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      this.logError("startBlur failed", { status: response.status, errorText });
      throw new Error(`Blur server error: ${errorText}`);
    }

    const data: AssignResponse = await response.json();

    if (!data.success) {
      this.logError("startBlur unsuccessful", data);
      throw new Error(data.message || "Failed to start blur");
    }

    this.activeWorkerId = data.worker_id;
    this.log("startBlur finished", {
      workerId: data.worker_id,
      blurOutputUrl: data.blur_output_url,
    });

    return {
      workerId: data.worker_id,
      blurOutputUrl: data.blur_output_url,
    };
  };

  stopBlur = async (): Promise<void> => {
    if (!this.activeWorkerId) {
      this.log("stopBlur skipped - no active worker");
      return;
    }

    this.log("stopBlur started", { workerId: this.activeWorkerId });

    try {
      const response = await fetch(`${this.baseUrl}/api/worker/release`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ worker_id: this.activeWorkerId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        this.logError("stopBlur failed", {
          status: response.status,
          errorText,
        });
      } else {
        this.log("stopBlur finished");
      }
    } catch (error) {
      this.logError("stopBlur error", error);
    } finally {
      this.activeWorkerId = null;
    }
  };

  getBlurStatus = async (
    channelName: string
  ): Promise<BlurStatusResponse> => {
    this.log("getBlurStatus started", { channelName });

    const response = await fetch(
      `${this.baseUrl}/api/blur/status/${channelName}`
    );

    if (!response.ok) {
      const errorText = await response.text();
      this.logError("getBlurStatus failed", {
        status: response.status,
        errorText,
      });
      throw new Error(`Blur status check failed: ${errorText}`);
    }

    const data: BlurStatusResponse = await response.json();
    this.log("getBlurStatus finished", data);
    return data;
  };

  getWorkers = async (): Promise<WorkersResponse> => {
    this.log("getWorkers started");

    const response = await fetch(`${this.baseUrl}/api/workers`);

    if (!response.ok) {
      const errorText = await response.text();
      this.logError("getWorkers failed", { status: response.status, errorText });
      throw new Error(`Failed to get workers: ${errorText}`);
    }

    const data: WorkersResponse = await response.json();
    this.log("getWorkers finished", { total: data.total, idle: data.idle_count });
    return data;
  };

  hasAvailableWorker = async (): Promise<boolean> => {
    try {
      const data = await this.getWorkers();
      return data.idle_count > 0;
    } catch {
      return false;
    }
  };

  getActiveWorkerId = (): string | null => {
    return this.activeWorkerId;
  };

  setActiveWorkerId = (workerId: string | null): void => {
    this.activeWorkerId = workerId;
  };
}

export const blurServerService = new BlurServerService();
export default BlurServerService;
